/**
 * Reads/writes for the "wall time" storage model: a time entry's `startedAt`
 * holds the author's wall-clock digits typed into the Log Time form, written
 * AS a UTC `Date` (a Kyiv 09:00 is stored as `…T09:00:00.000Z`) rather than the
 * real instant that clock reading fell at. The zone the author was in travels
 * alongside it, in a separate field, for display and for the server's own
 * bookkeeping — never through it here.
 *
 * Every helper below is therefore forbidden from touching the HOST's zone:
 * no `getHours`/`getDate`/`setHours`, no `toLocale*` without `timeZone: 'UTC'`,
 * no `new Date('YYYY-MM-DDTHH:mm')` without a trailing `Z`. Reading the UTC
 * fields of a `Date` (or formatting with `timeZone: 'UTC'`) is exactly reading
 * the digits the author typed, on any machine this code runs on. [HW-916]
 *
 * The one exception is a LEGACY row (null `timezone`), whose `startedAt` is
 * still the real instant it was stored as before this model. The backfill
 * rewrites those rows into wall digits, but it runs after the frontend ships,
 * and a browser tab opened before the deploy or a seed script can go on
 * writing legacy rows afterwards. Until they are converted, such a row has to
 * read exactly as it always did — its instant seen through the viewer's own
 * zone, named explicitly — so `entryStartWall`/`entryEndWall` below turn it
 * into that reading first. Every reader of an entry's clock or day goes
 * through them, with one deliberate exception: `entry-lock.ts`'s
 * `entryMonthLabel`, which names the month the SERVER's own cutoff counts —
 * a UTC month — and so reads `startedAt`'s raw digits on purpose, because it
 * is that server rule being reported, not the day the row is filed under
 * everywhere else. The primitives after these two take digits that are
 * already wall digits (an entry passed through them, or a window edge).
 * [HW-916]
 */

import { wallDigitsIn } from '@hygge/shared';
import { viewerTimeZone } from '@/lib/timezone';

/** What the legacy-aware readers need of an entry. `timezone` absent reads as legacy too. */
export interface EntryWallSource {
  startedAt: string;
  endedAt?: string | null;
  timezone?: string | null;
}

/**
 * `iso` as wall digits: itself on a row that has a zone, and on a legacy row
 * its instant read in the viewer's zone and written as UTC digits — the
 * reading the same row had on screen before HW-916. An unparsable string comes
 * back untouched, so the primitives below still blank the cell rather than
 * throw out of a list render. `wallDigitsIn` works in whole seconds; the
 * milliseconds are put back so a pass-through of the seconds loses nothing.
 */
function wallReading(
  iso: string,
  timezone: string | null | undefined,
  viewerZone: string | undefined
): string {
  if (timezone != null) return iso;
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return iso;
  const wall = wallDigitsIn(viewerZone ?? viewerTimeZone(), instant);
  wall.setUTCMilliseconds(instant.getUTCMilliseconds());
  return wall.toISOString();
}

/**
 * An entry's `startedAt` as the wall digits every reader should see — see the
 * header for the legacy row this exists for. `== null`, so an entry shape that
 * carries no zone at all is treated as the legacy row it most likely is.
 *
 * `viewerZone` is for a caller reading a whole list: it resolves the zone once
 * and hands it to every row, rather than asking `Intl` again per legacy row and
 * per sort comparison. Left out, the zone is looked up now — and only for a
 * legacy row — so a single-entry reader needs nothing. It is resolved per call,
 * never cached here, because a laptop that changes zone must read the next
 * render in the new one. [HW-916]
 */
export function entryStartWall(entry: EntryWallSource, viewerZone?: string): string {
  return wallReading(entry.startedAt, entry.timezone, viewerZone);
}

/** `entryStartWall` for the `endedAt`; null on an entry no stop ever closed. [HW-916] */
export function entryEndWall(entry: EntryWallSource, viewerZone?: string): string | null {
  return entry.endedAt ? wallReading(entry.endedAt, entry.timezone, viewerZone) : null;
}

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const CLOCK = /^\d{2}:\d{2}$/;

/**
 * The calendar-day key ("YYYY-MM-DD") an entry's `startedAt` was filed under —
 * the UTC digits, which under this model ARE the author's own day. Accepts any
 * ISO shape the API might send (an offset form, fractional seconds, …) by
 * round-tripping through `toISOString()` first. '' for a string that does not
 * parse to an instant at all, so a corrupt row blanks a cell instead of
 * throwing out of a list render.
 */
export function entryDayKey(iso: string): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return '';
  return instant.toISOString().slice(0, 10);
}

/**
 * The "HH:mm" the author typed, read straight off the UTC digits — see
 * {@link entryDayKey} for why that is the right field and not a bug. '' on an
 * unparsable instant, matching `entryDayKey`.
 */
export function entryClock(iso: string): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return '';
  return instant.toISOString().slice(11, 16);
}

/**
 * The reverse of the pair above: a day key plus the "HH:mm" the Log Time form
 * holds, turned into the `Date` the API sends as `startedAt`. Written as UTC
 * digits, deliberately — the number the server stores must be the digits the
 * form showed, not that instant converted through any zone.
 *
 * null on anything that is not exactly `YYYY-MM-DD` / `HH:mm`, and null on a
 * calendar date that does not exist (a Feb 30 or a DST-skipped local time have
 * no meaning here — this model has no "local", but a bad manual edit could
 * still hand in one): `Date` would otherwise silently roll it into the next
 * month, which the round-trip check below catches by comparing the day key
 * that comes back against the one asked for.
 */
export function wallInstant(dayKey: string, clock: string): Date | null {
  if (!DAY_KEY.test(dayKey) || !CLOCK.test(clock)) return null;
  const instant = new Date(`${dayKey}T${clock}:00.000Z`);
  if (Number.isNaN(instant.getTime())) return null;
  return instant.toISOString().slice(0, 10) === dayKey ? instant : null;
}

/** A picker's from/to day keys turned into the UTC instants that bound every entry filed on those calendar days, inclusive of both ends. */
export function dayKeyWindow(fromKey: string, toKey: string): { from: string; to: string } {
  return { from: `${fromKey}T00:00:00.000Z`, to: `${toKey}T23:59:59.999Z` };
}

/**
 * Display formatting for an entry's `startedAt`, e.g. "Sep 7" for a list row.
 * `timeZone: 'UTC'` is not a default that happens to be right here — it is the
 * only correct reading under this model, since the UTC digits are the wall
 * digits. '' for an unparsable instant, so a bad row renders a blank cell.
 */
export function formatEntryDate(iso: string, opts: Intl.DateTimeFormatOptions): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return '';
  return instant.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}
